import React, { ReactNode, useContext, useEffect } from 'react';
import Link from 'next/link';
import Modal from '@common/modal';
import { ACCAResultSaveContext } from '@components/acca/result/save';
import { TipType, TipOrder } from '@domain/constant/tipType';
import { RouteUrl } from '@utils/routeUtil';

import classnames from 'classnames/bind';
import styles from './additionalTasksModal.module.scss';
const cx = classnames.bind(styles);

interface AdditionalTasksModalProps {
  visible: boolean;
  onApplyLater: () => void;
}

const TaskCompRecord: Record<TipType, ReactNode> = {
  LIKERT: (
    <li key={'LIKERT'} className={cx('listItem', 'report', 'type1')}>
      <div className={cx('cardTitle')}>나 알아보기</div>
      <div className={cx('type')}>셀프 리포트</div>
    </li>
  ),
  META: (
    <li key={'META'} className={cx('listItem', 'report', 'type2')}>
      <div className={cx('cardTitle')}>
        타인 관점에서
        <br />나 알아보기
      </div>
      <div className={cx('type')}>셀프 리포트</div>
    </li>
  ),
  BIPOLAR: (
    <li key={'BIPOLAR'} className={cx('listItem', 'report', 'type3')}>
      <div className={cx('cardTitle')}>
        하나만
        <br />
        선택하기
      </div>
      <div className={cx('type')}>셀프 리포트</div>
    </li>
  ),
  MULTIPLE_CHOICE: (
    <li key={'MULTIPLE_CHOICE'} className={cx('listItem', 'report', 'type4')}>
      <div className={cx('cardTitle')}>
        여러개
        <br />
        선택하기
      </div>
      <div className={cx('type')}>셀프 리포트</div>
    </li>
  ),
  GAME: <></>,
  MRT: (
    <li key={'MRT'} className={cx('listItem', 'game', 'type1')}>
      <div className={cx('cardTitle')}>도형 회전하기</div>
      <div className={cx('type')}>게임</div>
    </li>
  ),
  RPS: (
    <li key={'RPS'} className={cx('listItem', 'game', 'type2')}>
      <div className={cx('cardTitle')}>가위바위보</div>
      <div className={cx('type')}>게임</div>
    </li>
  ),
  PM: (
    <li key={'PM'} className={cx('listItem', 'game', 'type3')}>
      <div className={cx('cardTitle')}>약속정하기</div>
      <div className={cx('type')}>게임</div>
    </li>
  ),
  RMT: (
    <li key={'RMT'} className={cx('listItem', 'game', 'type4')}>
      <div className={cx('cardTitle')}>길 만들기</div>
      <div className={cx('type')}>게임</div>
    </li>
  ),
  PCT2: (
    <li key={'PCT2'} className={cx('listItem', 'game', 'type5')}>
      <div className={cx('cardTitle')}>마법약 만들기</div>
      <div className={cx('type')}>게임</div>
    </li>
  ),
  FNB: (
    <li key={'FNB'} className={cx('listItem', 'game', 'type6')}>
      <div className={cx('cardTitle')}>
        도형 순서
        <br />
        기억하기
      </div>
      <div className={cx('type')}>게임</div>
    </li>
  ),
  OTN: (
    <li key={'OTN'} className={cx('listItem', 'game', 'type7')}>
      <div className={cx('cardTitle')}>숫자 누르기</div>
      <div className={cx('type')}>게임</div>
    </li>
  ),
  WNC: (
    <li key={'WNC'} className={cx('listItem', 'game', 'type8')}>
      <div className={cx('cardTitle')}>개수 비교하기</div>
      <div className={cx('type')}>게임</div>
    </li>
  ),
  HAS: (
    <li key={'HAS'} className={cx('listItem', 'game', 'type9')}>
      <div className={cx('cardTitle')}>
        고양이
        <br />
        술래잡기
      </div>
      <div className={cx('type')}>게임</div>
    </li>
  ),

  VIDEO: <></>,
  VIDEO_BASIC_EXPERIENCE: (
    <li key={'VIDEO_BASIC_EXPERIENCE'} className={cx('listItem', 'interview', 'type2')}>
      <div className={cx('cardTitle')}>경험면접</div>
      <div className={cx('type')}>인터뷰</div>
    </li>
  ),
  VIDEO_BASIC_VALUES: (
    <li key={'VIDEO_BASIC_VALUES'} className={cx('listItem', 'interview', 'type3')}>
      <div className={cx('cardTitle')}>가치관 면접</div>
      <div className={cx('type')}>인터뷰</div>
    </li>
  ),
  VIDEO_BASIC_FREE_CHOICE: (
    <li key={'VIDEO_BASIC_FREE_CHOICE'} className={cx('listItem', 'interview', 'type4')}>
      <div className={cx('cardTitle')}>질문선택 면접</div>
      <div className={cx('type')}>인터뷰</div>
    </li>
  ),
  VIDEO_BASIC_SITUATION: (
    <li key={'VIDEO_BASIC_SITUATION'} className={cx('listItem', 'interview', 'type8')}>
      <div className={cx('cardTitle')}>상황면접</div>
      <div className={cx('type')}>인터뷰</div>
    </li>
  ),
};

const AdditionalTasksModal = ({ visible, onApplyLater }: AdditionalTasksModalProps) => {
  const { tasks, setTasks } = useContext(ACCAResultSaveContext);

  const taskEl = tasks
    .sort((a, b) => {
      return TipOrder.indexOf(a.taskCode) - TipOrder.indexOf(b.taskCode);
    })
    .map((item, idx) => {
      return TaskCompRecord[item.taskCode];
    });

  useEffect(() => {
    if (!visible) {
      setTasks([]);
    }
  }, [visible]);

  return (
    <Modal visible={visible}>
      <div className={cx('layer')}>
        <em className={cx('title')}>추가 과제를 응시해 주세요!</em>

        <p className={cx('desc')}>
          결과표 생성 및 타 기업 제출을 위해서 기본 역검 전체 응시가 필요해요.
          <br />
          <span className={cx('emphasis')}>{`잡다 사이트 > 역검센터의 [이어하기]`}</span>를 통해
          <br />
          아래의 과제만 추가 응시하면 응시가 완료되어 결과를 확인할 수 있어요.
        </p>

        <div className={cx('scrollArea')}>
          <div className={cx('scrollInner')}>
            <ul className={cx('problemList')}>{taskEl}</ul>
          </div>
        </div>

        <div className={cx('btnArea')}>
          <a className={cx('btn', 'type1')} role="button" onClick={onApplyLater}>
            나중에 응시하기
          </a>

          <Link href={RouteUrl.ACCA_TEST_GATE} legacyBehavior>
            <a className={cx('btn', 'type2')} role="button">
              추가 과제 응시하기
            </a>
          </Link>
        </div>

        <a className={cx('btnClose')} role="button">
          <span className={cx('hidden')}>close</span>
        </a>
      </div>
    </Modal>
  );
};

export default AdditionalTasksModal;
